import { teamsClient } from '@atlassian/teams-client';
import { type TeamMembership } from '@atlassian/teams-client/types';

import { logInfoMessage } from '../../../../common/utils/sentry';
import { addNewMembertoTeamSubExp } from '../../../../common/utils/ufo';
import type { StoreApi } from '../../types';

import { inviteMemberState } from './utils';

export const inviteAgentsToTeam =
	(
		teamId: string,
		members: {
			id: string;
			email?: string;
			avatarUrl?: string;
			name: string;
		}[],
	) =>
	async ({ getState, setState }: StoreApi) => {
		addNewMembertoTeamSubExp.addMetadata({
			apiVersion: 'v4',
		});
		const addMembershipResponse = await teamsClient.addUsersToTeam(
			teamId,
			members.map((member) => ({
				accountId: member.id,
			})),
		);

		const memberships = addMembershipResponse.members.reduce<Required<TeamMembership>[]>(
			(acc, partial) => {
				if (!partial.accountId) {
					logInfoMessage('teamInvitationInvalidResponse', {
						message: 'Membership in response was invalid',
						member: partial,
						response: addMembershipResponse,
					});

					return acc;
				}

				const user = members.find((member) => member.id === partial.accountId);

				if (!user) {
					logInfoMessage('teamInvitationInvalidResponse', {
						message: 'Could not find user details for this member',
						member: partial,
						response: addMembershipResponse,
					});

					return acc;
				}

				acc.push({
					membershipId: {
						teamId,
						memberId: partial.accountId,
					},
					// state and role are deprecated in V4, so hard code them here
					state: 'FULL_MEMBER',
					role: 'REGULAR',
					user: {
						id: user.id,
						fullName: user.name,
						avatarUrl: user.avatarUrl,
						userType: 'user',
					},
				});

				return acc;
			},
			[],
		);

		const state = getState();

		// If focused team has changed whilst invite request was resolving, abort.
		if (teamId !== state.teamMembers.teamId) {
			return;
		}

		// Update the state with the new members.
		const nextState = inviteMemberState(state, memberships);
		setState(nextState);

		return memberships;
	};
